import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import SearchableSelect from './SearchableSelect';
import { usePrompt } from './ConfirmDialog';
import { catalogoVeiculosService } from '../services/catalogoVeiculos';
import { VeiculoMarca, VeiculoModelo } from '../types';

interface Props {
  marcaId: number | string | '';
  modeloId: number | string | '';
  onChange: (campos: { marca_id: string; modelo_id: string }) => void;
  /** Sem permissão de criar veículo, os botões de cadastrar não aparecem. */
  podeCadastrar?: boolean;
  disabled?: boolean;
}

/**
 * Marca e modelo do veículo, vindos do catálogo da empresa.
 *
 * Duas regras sustentam a tela:
 *
 * O modelo depende da marca. Sem marca escolhida o campo fica DESABILITADO, e
 * não escondido: sumir com ele faria o formulário pular e esconderia que um
 * modelo será exigido. Desabilitado com explicação ensina a dependência.
 *
 * O botão de cadastrar não é um atalho para encher o catálogo. O servidor
 * devolve a entrada existente quando o nome já existe com outra grafia, e pede
 * decisão quando o nome é apenas parecido. Esta tela apenas reage aos três
 * desfechos — a regra vive no servidor, onde ninguém a contorna.
 */
const SelecaoDeMarcaEModelo: React.FC<Props> = ({
  marcaId,
  modeloId,
  onChange,
  podeCadastrar = false,
  disabled = false,
}) => {
  const prompt = usePrompt();
  const [marcas, setMarcas] = useState<VeiculoMarca[]>([]);
  const [modelos, setModelos] = useState<VeiculoModelo[]>([]);
  const [carregandoModelos, setCarregandoModelos] = useState(false);

  const marcaSelecionada = marcaId ? Number(marcaId) : null;

  const carregarMarcas = useCallback(async () => {
    try {
      const res = await catalogoVeiculosService.listarMarcas();
      setMarcas(res.data);
    } catch {
      toast.error('Não foi possível carregar as marcas.');
    }
  }, []);

  useEffect(() => {
    carregarMarcas();
  }, [carregarMarcas]);

  // Nenhuma requisição de modelos parte sem marca: a rota exige uma.
  useEffect(() => {
    if (!marcaSelecionada) {
      setModelos([]);
      return;
    }
    let cancelado = false;
    setCarregandoModelos(true);
    catalogoVeiculosService
      .listarModelos(marcaSelecionada)
      .then((res) => {
        if (!cancelado) setModelos(res.data);
      })
      .catch(() => {
        if (!cancelado) toast.error('Não foi possível carregar os modelos desta marca.');
      })
      .finally(() => {
        if (!cancelado) setCarregandoModelos(false);
      });
    return () => {
      cancelado = true;
    };
  }, [marcaSelecionada]);

  const opcoesDeMarca = useMemo(
    () => marcas.map((m) => ({ value: String(m.id), label: m.nome })),
    [marcas]
  );
  const opcoesDeModelo = useMemo(
    () => modelos.map((m) => ({ value: String(m.id), label: m.nome })),
    [modelos]
  );

  const trocarMarca = (valor: string) => {
    // Trocar a marca limpa o modelo: mantê-lo produziria um par inválido, que o
    // servidor recusaria com 422 depois de a pessoa já ter preenchido o resto.
    //
    // A limpeza só acontece aqui, no evento de troca. Em modo edição, marca e
    // modelo chegam juntos num único setFormData e NÃO passam por esta função —
    // se passassem, abrir um veículo para editar apagaria o modelo dele na tela.
    onChange({ marca_id: valor, modelo_id: '' });
  };

  const trocarModelo = (valor: string) => {
    onChange({ marca_id: String(marcaId ?? ''), modelo_id: valor });
  };

  /** Cadastro de marca ou modelo, com os três desfechos possíveis do servidor. */
  const cadastrar = async (tipo: 'marca' | 'modelo') => {
    const ehMarca = tipo === 'marca';
    const nomeDaMarca = marcas.find((m) => m.id === marcaSelecionada)?.nome ?? '';

    const nome = await prompt({
      title: ehMarca ? 'Nova marca' : `Novo modelo de ${nomeDaMarca}`,
      input: {
        type: 'text',
        label: ehMarca ? 'Nome da marca' : 'Nome do modelo',
        placeholder: ehMarca ? 'Volkswagen' : 'Gol',
        required: true,
      },
    });
    if (!nome?.trim()) return;

    await enviar(tipo, nome.trim(), false);
  };

  const enviar = async (tipo: 'marca' | 'modelo', nome: string, confirmar: boolean) => {
    const ehMarca = tipo === 'marca';
    try {
      const res = ehMarca
        ? await catalogoVeiculosService.criarMarca(nome, confirmar)
        : await catalogoVeiculosService.criarModelo(marcaSelecionada!, nome, confirmar);

      // Aviso informativo, nunca erro: a pessoa conseguiu o que queria, e não é
      // culpa dela não ter procurado antes.
      toast.success(res.message ?? (ehMarca ? 'Marca cadastrada.' : 'Modelo cadastrado.'));

      // O item novo entra na lista local ANTES de ser selecionado, e nao
      // depois de a busca voltar do servidor.
      //
      // O SearchableSelect monta o texto exibido procurando o valor entre as
      // opcoes. Se o valor chega antes da opcao, o campo aparece vazio e o
      // combobox limpa a selecao na interacao seguinte: a pessoa cadastra o
      // modelo, ve o campo em branco, salva, e o formulario manda o valor
      // antigo. A insercao otimista fecha essa janela por completo.
      if (ehMarca) {
        const nova = res.data as VeiculoMarca;
        setMarcas((atual) => (atual.some((m) => m.id === nova.id) ? atual : [...atual, nova]));
        onChange({ marca_id: String(nova.id), modelo_id: '' });
        carregarMarcas();
      } else {
        const novo = res.data as VeiculoModelo;
        setModelos((atual) => (atual.some((m) => m.id === novo.id) ? atual : [...atual, novo]));
        onChange({ marca_id: String(marcaId ?? ''), modelo_id: String(novo.id) });
      }
    } catch (erro: any) {
      const resposta = erro?.response;
      if (resposta?.status === 409 && resposta.data?.codigo === 'nome_parecido') {
        await reconciliar(tipo, resposta.data.data);
        return;
      }
      const msg = resposta?.data?.errors?.nome?.[0] ?? resposta?.data?.message;
      toast.error(msg ?? 'Não foi possível cadastrar.');
    }
  };

  /**
   * O servidor achou nomes parecidos. Só a pessoa sabe se digitou errado ou se
   * é realmente uma marca nova, então a decisão volta para ela — em vez de o
   * sistema bloquear (o que a empurraria a inventar variações até passar).
   */
  const reconciliar = async (
    tipo: 'marca' | 'modelo',
    dados: { nome_solicitado: string; sugestoes: Array<{ id: number; nome: string }> }
  ) => {
    const rotulo = tipo === 'marca' ? 'marca' : 'modelo';
    const escolha = await prompt({
      title: `Já existe ${rotulo} parecid${tipo === 'marca' ? 'a' : 'o'}`,
      message: `Você digitou «${dados.nome_solicitado}».`,
      input: {
        type: 'select',
        label: 'O que você quer fazer?',
        options: [
          ...dados.sugestoes.map((s) => ({ value: String(s.id), label: `Usar «${s.nome}»` })),
          { value: 'novo', label: `Cadastrar «${dados.nome_solicitado}» mesmo assim` },
        ],
      },
    });
    if (!escolha) return;

    if (escolha === 'novo') {
      await enviar(tipo, dados.nome_solicitado, true);
      return;
    }
    // Usar a existente: nenhum cadastro é feito.
    // A sugestao veio do servidor e ja esta no catalogo, mas pode nao estar na
    // pagina carregada aqui. Mesmo cuidado da insercao otimista acima.
    if (tipo === 'marca') {
      const sugerida = dados.sugestoes.find((s) => String(s.id) === escolha);
      if (sugerida) {
        setMarcas((atual) => (atual.some((m) => m.id === sugerida.id)
          ? atual
          : [...atual, { ...sugerida, created_at: '', updated_at: '' } as VeiculoMarca]));
      }
      onChange({ marca_id: escolha, modelo_id: '' });
    } else {
      const sugerido = dados.sugestoes.find((s) => String(s.id) === escolha);
      if (sugerido) {
        setModelos((atual) => (atual.some((m) => m.id === sugerido.id)
          ? atual
          : [...atual, { ...sugerido, marca_id: marcaSelecionada!, created_at: '', updated_at: '' } as VeiculoModelo]));
      }
      onChange({ marca_id: String(marcaId ?? ''), modelo_id: escolha });
    }
  };

  const semMarca = !marcaSelecionada;

  return (
    <>
      <div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <SearchableSelect
              label="Marca"
              value={marcaId ? String(marcaId) : ''}
              onChange={trocarMarca}
              options={opcoesDeMarca}
              placeholder="Selecione a marca"
              required
              disabled={disabled}
            />
          </div>
          {podeCadastrar && (
            <button
              type="button"
              onClick={() => cadastrar('marca')}
              disabled={disabled}
              aria-label="Cadastrar nova marca"
              title="Cadastrar nova marca"
              className="h-[42px] w-[42px] flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <SearchableSelect
              label="Modelo"
              value={modeloId ? String(modeloId) : ''}
              onChange={trocarModelo}
              options={opcoesDeModelo}
              placeholder={semMarca ? 'Escolha a marca primeiro' : carregandoModelos ? 'Carregando...' : 'Selecione o modelo'}
              required
              disabled={disabled || semMarca}
            />
          </div>
          {podeCadastrar && (
            <button
              type="button"
              onClick={() => cadastrar('modelo')}
              disabled={disabled || semMarca}
              aria-label="Cadastrar novo modelo"
              title={semMarca ? 'Escolha a marca primeiro' : 'Cadastrar novo modelo'}
              className="h-[42px] w-[42px] flex items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default SelecaoDeMarcaEModelo;
